import type { ReactElement, ReactNode } from "react"
import { Fragment, useMemo, useState } from "react"
import { format, parseISO } from "date-fns"
import {
	IconCalendarOutline18 as CalendarIcon,
	IconCalendarXmarkOutline18 as CalendarXmarkIcon,
	IconArrowDashedRotateClockwiseOutline18 as CycleIcon,
	IconDotsOutline18 as DotsIcon,
	IconGauge2Outline18 as EstimateIcon,
	IconLink3Outline18 as LinkIcon,
	IconLinkSlashOutline18 as LinkSlashIcon,
	IconFlag3Outline18 as MilestoneIcon,
	IconListTreeOutline18 as ParentIcon,
	IconShippingContainerOutline18 as ReleaseIcon,
} from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"

import { cn } from "@productlane/lib/src/browser"
import {
	Button,
	DropdownMenu,
	DropdownMenuItem,
	IconButton,
	Input,
	IssueStatusIcon,
	Loader,
	Popover,
} from "@productlane/ui"
import { Calendar } from "@productlane/ui/src/components/internal/calendar"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import {
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@productlane/ui/src/components/internal/dropdown"

import type { MoreOptionField } from "./create-issue-more-options-fields"
import type { DataOptionType } from "@/components/popover-button"
import type { RouterOutputs } from "@/lib/api"
import { PopoverButton } from "@/components/popover-button"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import {
	availableMoreOptions,
	linkLabel,
	normalizeLinkUrl,
} from "./create-issue-more-options-fields"
import {
	createIssueShortcutHint,
	ShortcutTooltipLabel,
} from "./create-issue-shortcuts"
import {
	formatDueDateHint,
	getDueDatePresets,
	parseDueDateQuery,
	toDueDate,
} from "./due-date-options"

export interface ParentIssue {
	id: string
	identifier: string
	title: string
}

export interface IssueLink {
	url: string
	title?: string
}

export interface MoreOptionValues {
	dueDate?: string
	cycleId?: string
	estimate?: string
	projectMilestoneId?: string
	releaseId?: string
	parent?: ParentIssue
	links: IssueLink[]
}

const OPTION_QUERY = { cacheTime: Infinity, staleTime: Infinity } as const
// Capped so a long name truncates instead of pushing the next chips onto a new row.
export const CHIP_CLASS = "max-w-48 dark:bg-gray-800"

// Stands in for a chip that has no value yet: its picker opens at the ⋯ button and the row holds still.
const AT_MORE_BUTTON = (
	<span
		aria-hidden
		data-testid="more-options-anchor"
		className="pointer-events-none absolute inset-0"
	/>
)

const MENU_ITEMS: Record<
	MoreOptionField,
	{ label: string; Icon: typeof CalendarIcon }
> = {
	dueDate: { label: "Set due date", Icon: CalendarIcon },
	cycle: { label: "Add to cycle", Icon: CycleIcon },
	estimate: { label: "Set estimate", Icon: EstimateIcon },
	milestone: { label: "Add to milestone", Icon: MilestoneIcon },
	link: { label: "Add link...", Icon: LinkIcon },
	release: { label: "Add to release", Icon: ReleaseIcon },
	parent: { label: "Set parent issue", Icon: ParentIcon },
}

// Fields that become a chip once set, in Linear's order. Links render one chip each.
const CHIP_ORDER = [
	"dueDate",
	"cycle",
	"estimate",
	"milestone",
	"release",
	"parent",
] as const satisfies readonly MoreOptionField[]

export type TeamIssueSettings = RouterOutputs["linear"]["teamIssueSettings"]

interface PickerChoices {
	options: DataOptionType[]
	isLoading: boolean
}

export type MoreOptionChoices = Record<
	"cycle" | "estimate" | "milestone" | "release",
	PickerChoices
>

// Loaded with the dialog, not on first open, so a shortcut knows whether there is anything to pick.
export function useMoreOptionChoices({
	isOpen,
	teamId,
	projectId,
	settings,
}: {
	isOpen: boolean
	teamId: string
	projectId?: string
	settings?: TeamIssueSettings
}): MoreOptionChoices {
	const { data: cycles, isFetching: isFetchingCycles } =
		api.linear.teamCycles.useQuery(
			{ teamId },
			{
				...OPTION_QUERY,
				enabled: isOpen && !!teamId && !!settings?.cyclesEnabled,
				retry: false,
			},
		)
	const { data: milestones, isFetching: isFetchingMilestones } =
		api.linear.projectMilestones.useQuery(
			{ projectId: projectId ?? "" },
			{ ...OPTION_QUERY, enabled: isOpen && !!projectId, retry: false },
		)
	const { data: releases, isFetching: isFetchingReleases } =
		api.linear.releases.useQuery(undefined, {
			...OPTION_QUERY,
			enabled: isOpen,
			retry: false,
		})

	return useMemo(
		() => ({
			cycle: {
				options: (cycles ?? []).map((cycle) => ({
					value: cycle.id,
					label: cycle.label,
				})),
				isLoading: isFetchingCycles && !cycles,
			},
			estimate: {
				options: (settings?.estimateOptions ?? []).map((option) => ({
					value: String(option.value),
					label: option.label,
				})),
				isLoading: false,
			},
			milestone: {
				options: (milestones ?? []).map((milestone) => ({
					value: milestone.id,
					label: milestone.name,
				})),
				isLoading: isFetchingMilestones && !milestones,
			},
			release: {
				options: (releases ?? []).map((release) => ({
					value: release.id,
					label: (
						<div className="flex items-center gap-2">
							<span className="truncate">{release.name}</span>
							<span className="flex-none text-tertiary">
								{release.pipelineName}
							</span>
						</div>
					),
					triggerLabel: release.name,
				})),
				isLoading: isFetchingReleases && !releases,
			},
		}),
		[
			cycles,
			isFetchingCycles,
			settings?.estimateOptions,
			milestones,
			isFetchingMilestones,
			releases,
			isFetchingReleases,
		],
	)
}

interface Props {
	isOpen: boolean
	projectId?: string
	settings?: TeamIssueSettings
	choices: MoreOptionChoices
	values: MoreOptionValues
	onChange: (patch: Partial<MoreOptionValues>) => void
	active: MoreOptionField | null
	onActiveChange: (field: MoreOptionField | null) => void
}

export const CreateIssueMoreOptions = ({
	isOpen,
	projectId,
	settings,
	choices,
	values,
	onChange,
	active,
	onActiveChange,
}: Props) => {
	const [menuOpen, setMenuOpen] = useState(false)
	// "Custom..." in the menu goes straight to the calendar instead of the quick picks.
	const [dueDateCustom, setDueDateCustom] = useState(false)

	const hasValue: Record<MoreOptionField, boolean> = {
		dueDate: !!values.dueDate,
		cycle: !!values.cycleId,
		estimate: !!values.estimate,
		milestone: !!values.projectMilestoneId,
		release: !!values.releaseId,
		parent: !!values.parent,
		link: false,
	}
	const pending = isOpen && active && !hasValue[active] ? active : null
	const openChange = (field: MoreOptionField) => (open: boolean) => {
		if (open) onActiveChange(field)
		else if (active === field) onActiveChange(null)
	}
	// Opens after the menu has closed, so its focus return can't dismiss the picker.
	const openFromMenu = (field: MoreOptionField) =>
		setTimeout(() => onActiveChange(field), 0)
	const tooltip = (field: MoreOptionField) => (
		<ShortcutTooltipLabel label={MENU_ITEMS[field].label} picker={field} />
	)
	const menuFields = availableMoreOptions({
		cyclesEnabled: settings?.cyclesEnabled ?? false,
		hasEstimates: (settings?.estimateOptions.length ?? 0) > 0,
		hasProject: !!projectId,
	}).filter((field) => field === "link" || !hasValue[field])

	// Without a trigger it renders the field's chip; with one, a picker opened from that element.
	const picker = (field: MoreOptionField, trigger?: ReactElement) => {
		const chipTooltip = trigger ? undefined : tooltip(field)
		switch (field) {
			case "dueDate":
				return (
					<DueDateChip
						value={values.dueDate}
						startInCalendar={dueDateCustom}
						trigger={trigger}
						tooltip={chipTooltip}
						open={active === "dueDate"}
						onOpenChange={(open) => {
							if (!open) setDueDateCustom(false)
							openChange("dueDate")(open)
						}}
						onChange={(dueDate) => onChange({ dueDate })}
					/>
				)
			case "cycle":
				return (
					<PopoverButton
						variant="outline"
						className={CHIP_CLASS}
						data={choices.cycle.options}
						selected={values.cycleId}
						name="Cycle"
						icon={CycleIcon}
						placeholder="Add to cycle..."
						resetOption="No cycle"
						resetIcon={CycleIcon}
						isLoading={choices.cycle.isLoading}
						tooltip={chipTooltip}
						numberShortcuts
						open={active === "cycle"}
						onOpenChange={openChange("cycle")}
						onChange={(cycleId) => onChange({ cycleId: cycleId ?? undefined })}
					>
						{trigger}
					</PopoverButton>
				)
			case "estimate":
				return (
					<PopoverButton
						variant="outline"
						className={CHIP_CLASS}
						data={choices.estimate.options}
						selected={values.estimate}
						name="Estimate"
						icon={EstimateIcon}
						placeholder="Set estimate..."
						resetOption="No estimate"
						resetIcon={EstimateIcon}
						tooltip={chipTooltip}
						numberShortcuts
						open={active === "estimate"}
						onOpenChange={openChange("estimate")}
						onChange={(estimate) =>
							onChange({ estimate: estimate ?? undefined })
						}
					>
						{trigger}
					</PopoverButton>
				)
			case "milestone":
				return (
					<PopoverButton
						variant="outline"
						className={CHIP_CLASS}
						data={choices.milestone.options}
						selected={values.projectMilestoneId}
						name="Milestone"
						icon={MilestoneIcon}
						placeholder="Add to milestone..."
						resetOption="No milestone"
						resetIcon={MilestoneIcon}
						isLoading={choices.milestone.isLoading}
						tooltip={chipTooltip}
						numberShortcuts
						open={active === "milestone"}
						onOpenChange={openChange("milestone")}
						onChange={(projectMilestoneId) =>
							onChange({ projectMilestoneId: projectMilestoneId ?? undefined })
						}
					>
						{trigger}
					</PopoverButton>
				)
			case "release":
				return (
					<PopoverButton
						variant="outline"
						className={CHIP_CLASS}
						data={choices.release.options}
						selected={values.releaseId}
						name="Release"
						icon={ReleaseIcon}
						placeholder="Add to release..."
						resetOption="No release"
						resetIcon={ReleaseIcon}
						isLoading={choices.release.isLoading}
						tooltip={chipTooltip}
						numberShortcuts
						open={active === "release"}
						onOpenChange={openChange("release")}
						onChange={(releaseId) =>
							onChange({ releaseId: releaseId ?? undefined })
						}
					>
						{trigger}
					</PopoverButton>
				)
			case "parent":
				return (
					<ParentIssueChip
						value={values.parent}
						trigger={trigger}
						open={active === "parent"}
						onOpenChange={openChange("parent")}
						onChange={(parent) => onChange({ parent })}
					/>
				)
			case "link":
				// Each added link gets its own chip, so the form itself always opens at ⋯.
				return (
					<AddLinkPicker
						trigger={trigger ?? AT_MORE_BUTTON}
						open={active === "link"}
						onOpenChange={openChange("link")}
						onAdd={(link) => {
							onChange({ links: [...values.links, link] })
							onActiveChange(null)
						}}
					/>
				)
		}
	}

	return (
		<>
			{CHIP_ORDER.filter((field) => isOpen && hasValue[field]).map((field) => (
				<Fragment key={field}>{picker(field)}</Fragment>
			))}

			{values.links.map((link, index) => (
				<LinkChip
					key={`${link.url}-${index}`}
					link={link}
					onRemove={() =>
						onChange({ links: values.links.filter((_, i) => i !== index) })
					}
				/>
			))}

			<div className="relative flex">
				{pending && (
					<Fragment key={pending}>{picker(pending, AT_MORE_BUTTON)}</Fragment>
				)}
				<DropdownMenu
					open={menuOpen}
					onOpenChange={setMenuOpen}
					trigger={
						<IconButton
							variant="outline"
							className={CHIP_CLASS}
							Icon={DotsIcon}
							aria-label="More options"
						/>
					}
					contentProps={{ align: "start", side: "bottom" }}
				>
					{menuFields.map((field) =>
						field === "dueDate" ? (
							<DropdownMenuSub key={field}>
								<DropdownMenuSubTrigger
									className="rounded-md"
									Icon={MENU_ITEMS.dueDate.Icon}
									shortcut={createIssueShortcutHint("dueDate")}
								>
									{MENU_ITEMS.dueDate.label}
								</DropdownMenuSubTrigger>
								<DropdownMenuPortal>
									<DropdownMenuSubContent
										className="w-64 p-0"
										sideOffset={6}
										// Keyboard opening focuses the submenu itself; hand that to the search field.
										onFocus={(event) => {
											if (event.target !== event.currentTarget) return
											event.currentTarget.querySelector("input")?.focus()
										}}
									>
										<DueDateOptions
											onSelect={(dueDate) => {
												onChange({ dueDate })
												setMenuOpen(false)
											}}
											onCustom={() => {
												setMenuOpen(false)
												setDueDateCustom(true)
												openFromMenu("dueDate")
											}}
										/>
									</DropdownMenuSubContent>
								</DropdownMenuPortal>
							</DropdownMenuSub>
						) : (
							<Fragment key={field}>
								{field === "parent" && <DropdownMenuSeparator />}
								<DropdownMenuItem
									Icon={MENU_ITEMS[field].Icon}
									shortcut={createIssueShortcutHint(field)}
									onSelect={() => openFromMenu(field)}
								>
									{MENU_ITEMS[field].label}
								</DropdownMenuItem>
							</Fragment>
						),
					)}
				</DropdownMenu>
			</div>
		</>
	)
}

// Linear's quick picks: type "24h", "7 days" or "Feb 9", or take a preset.
const DueDateOptions = ({
	onSelect,
	onCustom,
}: {
	onSelect: (value: string) => void
	onCustom: () => void
}) => {
	const [query, setQuery] = useState("")
	const isSearching = !!query.trim()
	const options = useMemo(() => {
		const now = new Date()
		return isSearching ? parseDueDateQuery(query, now) : getDueDatePresets(now)
	}, [query, isSearching])

	return (
		<Command
			shouldFilter={false}
			loop
			// Keeps Radix menu typeahead and arrow handling out of the search field.
			onKeyDown={(event) => {
				if (event.key !== "Escape" && event.key !== "Tab") {
					event.stopPropagation()
				}
			}}
		>
			<CommandInput
				autoFocus
				showIcon={false}
				placeholder="Try: 24h, 7 days, Feb 9"
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList>
				<CommandEmpty>No matching date</CommandEmpty>
				<CommandGroup>
					{!isSearching && (
						<CommandItem value="custom" Icon={CalendarIcon} onSelect={onCustom}>
							Custom...
						</CommandItem>
					)}
					{options.map((option) => (
						<CommandItem
							key={`${option.label}-${toDueDate(option.date)}`}
							value={`${option.label}-${toDueDate(option.date)}`}
							Icon={CalendarIcon}
							hint={formatDueDateHint(option.date)}
							onSelect={() => onSelect(toDueDate(option.date))}
						>
							{option.label}
						</CommandItem>
					))}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

const DueDateChip = ({
	value,
	startInCalendar,
	trigger,
	tooltip,
	open,
	onOpenChange,
	onChange,
}: {
	value?: string
	startInCalendar: boolean
	trigger?: ReactElement
	tooltip?: ReactNode
	open: boolean
	onOpenChange: (open: boolean) => void
	onChange: (value: string | undefined) => void
}) => {
	const date = value ? parseISO(value) : undefined
	const initialView = date || startInCalendar ? "calendar" : "options"
	const [view, setView] = useState<"options" | "calendar">(initialView)
	const [wasOpen, setWasOpen] = useState(open)
	if (open !== wasOpen) {
		setWasOpen(open)
		if (open) setView(initialView)
	}

	return (
		<Popover
			open={open}
			onOpenChange={onOpenChange}
			className={view === "options" ? "w-64 p-0" : "w-auto p-0"}
			triggerTooltip={tooltip}
			content={
				view === "options" ? (
					<DueDateOptions
						onSelect={(next) => {
							onChange(next)
							onOpenChange(false)
						}}
						onCustom={() => setView("calendar")}
					/>
				) : (
					<div className="flex flex-col">
						<Calendar
							mode="single"
							selected={date}
							defaultMonth={date}
							onSelect={(next) => {
								if (next) onChange(toDueDate(next))
								onOpenChange(false)
							}}
							autoFocus
						/>
						{date && (
							<div className="border-t border-gray-200 p-2 dark:border-gray-700/80">
								<Button
									variant="ghost"
									className="w-full justify-start"
									Icon={CalendarXmarkIcon}
									onClick={() => {
										onChange(undefined)
										onOpenChange(false)
									}}
								>
									Remove due date
								</Button>
							</div>
						)}
					</div>
				)
			}
		>
			{trigger ?? (
				<Button variant="outline" className={CHIP_CLASS} Icon={CalendarIcon}>
					{date ? `Due ${format(date, "MMM d")}` : "Due date"}
				</Button>
			)}
		</Popover>
	)
}

const ParentIssueChip = ({
	value,
	trigger,
	open,
	onOpenChange,
	onChange,
}: {
	value?: ParentIssue
	trigger?: ReactElement
	open: boolean
	onOpenChange: (open: boolean) => void
	onChange: (value: ParentIssue | undefined) => void
}) => {
	const [search, setSearch] = useState("")
	const [debounced, setDebounced] = useState("")
	useDebounce(() => setDebounced(search.trim()), 250, [search])

	const { data, isFetching } = api.linear.searchIssues.useQuery(
		{
			query: debounced || undefined,
			first: 20,
			excludeStatus: ["completed", "canceled"],
		},
		{ ...OPTION_QUERY, enabled: open, keepPreviousData: true },
	)

	const select = (parent: ParentIssue | undefined) => {
		onChange(parent)
		setSearch("")
		onOpenChange(false)
	}

	return (
		<Popover
			open={open}
			onOpenChange={onOpenChange}
			className="w-80 p-0"
			content={
				<Command shouldFilter={false}>
					<CommandInput
						autoFocus
						showIcon={false}
						value={search}
						onValueChange={setSearch}
						placeholder="Search issues..."
					/>
					<CommandList>
						{!data && isFetching ? (
							<div className="flex items-center justify-center py-6">
								<Loader />
							</div>
						) : (
							<CommandEmpty>No matching issues</CommandEmpty>
						)}
						<CommandGroup>
							{value && (
								<CommandItem
									value="no-parent"
									onSelect={() => select(undefined)}
								>
									<div className="flex w-full items-center gap-2">
										<ParentIcon className="h-4 w-4 flex-none" />
										<span className="flex-1 truncate">No parent issue</span>
									</div>
								</CommandItem>
							)}
							{(data?.nodes ?? []).map((issue) => (
								<CommandItem
									key={issue.id}
									value={issue.id}
									onSelect={() =>
										select({
											id: issue.id,
											identifier: issue.identifier,
											title: issue.title,
										})
									}
								>
									<div className="flex w-full min-w-0 items-center gap-2">
										<IssueStatusIcon
											status={issue.state.type}
											color={issue.state.color ?? ""}
											className="h-3.5 w-3.5 flex-none"
										/>
										<span className="flex-none text-tertiary">
											{issue.identifier}
										</span>
										<span className="flex-1 truncate">{issue.title}</span>
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			}
		>
			{trigger ?? (
				<Button
					variant="outline"
					className={cn(CHIP_CLASS, "max-w-64")}
					Icon={ParentIcon}
				>
					<span className="truncate">
						{value ? `Sub-issue of ${value.identifier}` : "Parent issue"}
					</span>
				</Button>
			)}
		</Popover>
	)
}

const AddLinkPicker = ({
	trigger,
	open,
	onOpenChange,
	onAdd,
}: {
	trigger: ReactElement
	open: boolean
	onOpenChange: (open: boolean) => void
	onAdd: (link: IssueLink) => void
}) => {
	const [url, setUrl] = useState("")
	const [title, setTitle] = useState("")

	const submit = () => {
		const normalized = normalizeLinkUrl(url)
		if (!normalized) {
			toast.info({
				title: "Enter a full link, like https://example.com",
				icon: LinkIcon,
			})
			return
		}
		onAdd({ url: normalized, title: title.trim() || undefined })
		setUrl("")
		setTitle("")
	}

	return (
		<Popover
			open={open}
			onOpenChange={onOpenChange}
			className="w-80 p-3"
			content={
				// Portaled outside the dialog's form, but React still bubbles submit to it.
				<form
					className="flex flex-col gap-2"
					onSubmit={(event) => {
						event.preventDefault()
						event.stopPropagation()
						submit()
					}}
				>
					<Input
						autoFocus
						name="issue-link-url"
						value={url}
						onChange={(event) => setUrl(event.target.value)}
						placeholder="https://..."
						aria-label="Link URL"
					/>
					<Input
						name="issue-link-title"
						value={title}
						onChange={(event) => setTitle(event.target.value)}
						placeholder="Title (optional)"
						aria-label="Link title"
					/>
					<Button type="submit">Add link</Button>
				</form>
			}
		>
			{trigger}
		</Popover>
	)
}

const LinkChip = ({
	link,
	onRemove,
}: {
	link: IssueLink
	onRemove: () => void
}) => {
	const [open, setOpen] = useState(false)
	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			className="w-72 p-2"
			content={
				<div className="flex flex-col gap-1">
					<div className="truncate px-2 py-1 text-xs text-tertiary">
						{link.url}
					</div>
					<Button
						variant="ghost"
						className="w-full justify-start"
						Icon={LinkSlashIcon}
						onClick={() => {
							setOpen(false)
							onRemove()
						}}
					>
						Remove link
					</Button>
				</div>
			}
		>
			<Button variant="outline" className={CHIP_CLASS} Icon={LinkIcon}>
				<span className="truncate">{linkLabel(link)}</span>
			</Button>
		</Popover>
	)
}
